import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { getUser } from '../../utils/auth';
import { useRoles } from '../../hooks/useRoles';
import { useLocations } from '../../hooks/useUserLocations';
import type { Role, RoleCategory } from '../../types';

const fetchStaff = async (locationId: string, page: number, pageSize: number) => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  // Only add locationId filter if it's provided
  if (locationId) {
    params.locationId = locationId;
  }

  // Use /users/staff endpoint to get only platform_staff users (not customers)
  const response = await axiosInstance.get(`/users/staff`, { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const filterOptions: FilterOption[] = [
  {
    field: 'role',
    label: 'Role',
    type: 'select',
    options: [
      { value: 'store_owner', label: 'Store Owner' },
      { value: 'store_manager', label: 'Store Manager' },
      { value: 'store_staff', label: 'Store Staff' },
    ],
  },
];

const getExportColumns = (roles: any[], locations: any[]): ColumnConfig[] => [
  {
    field: 'name',
    headerName: 'Name',
    valueFormatter: (_, row) => `${row.firstName} ${row.lastName}`,
  },
  { field: 'email', headerName: 'Email' },
  { field: 'phone', headerName: 'Phone' },
  {
    field: 'roles',
    headerName: 'Roles',
    valueFormatter: (value, row) => {
      // Try new multi-role system first
      if (value?.length) {
        return value.map((r: any) => r.name).join(', ');
      }
      // Fall back to userRoles junction table
      if (row.userRoles?.length) {
        return row.userRoles.map((ur: any) => ur.role?.name || 'Unknown').join(', ');
      }
      return '';
    },
  },
  {
    field: 'userLocations',
    headerName: 'Assigned Locations',
    valueFormatter: (value) => {
      if (!value?.length) return '';
      return value
        .map((ul: any) => {
          const location = locations?.find((l) => l.id === ul.locationId);
          return location?.name || ul.locationId;
        })
        .join(', ');
    },
  },
];

export const StaffList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const user = getUser();
  const isAdmin = user?.isAdmin || false;
  const locationId = user?.locationId;

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data: roles } = useRoles();
  const { data: locations } = useLocations();

  const { data, isLoading } = useQuery({
    queryKey: ['staff', locationId, paginationModel.page, paginationModel.pageSize, isAdmin],
    queryFn: () => fetchStaff(locationId || '', paginationModel.page, paginationModel.pageSize),
    enabled: isAdmin || !!locationId,
  });

  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((item: any) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const fullName = `${item.firstName} ${item.lastName}`;
        const matchesSearch =
          fullName.toLowerCase().includes(search) ||
          item.email?.toLowerCase().includes(search) ||
          item.phone?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Role filter - check against multi-role system
      if (activeFilters.role) {
        const userRoleSystemKeys = item.roles?.map((r: any) => r.systemKey) ||
          item.userRoles?.map((ur: any) => ur.role?.systemKey).filter(Boolean) ||
          item.roleSystemKeys ||
          [];
        if (!userRoleSystemKeys.includes(activeFilters.role)) {
          return false;
        }
      }

      return true;
    });
  }, [data?.data, searchValue, activeFilters]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      showMessage('Staff member deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete staff member', 'error');
    },
  });

  const handleDeleteClick = (id: string) => {
    setStaffToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (staffToDelete) {
      deleteMutation.mutate(staffToDelete);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  // Get color based on role category
  const getRoleCategoryColor = (category?: RoleCategory | null): 'primary' | 'secondary' | 'default' => {
    switch (category) {
      case 'administrative':
        return 'primary';
      case 'operational':
        return 'secondary';
      case 'combined':
      default:
        return 'default';
    }
  };

  // Get user's roles from various sources (multi-role system or userRoles junction)
  const getUserRoles = (row: any): Role[] => {
    // New multi-role: check roles array
    if (row.roles?.length) {
      return row.roles;
    }
    // Multi-role via userRoles junction table
    if (row.userRoles?.length) {
      return row.userRoles.map((ur: any) => ur.role).filter(Boolean);
    }
    return [];
  };

  const getLocationNames = (userLocations: any[] | undefined, legacyLocationId?: string, legacyLocation?: any) => {
    // First check new multi-location system
    if (userLocations?.length && locations) {
      return userLocations.map((ul) => {
        const location = locations.find((l) => l.id === ul.locationId);
        return {
          id: ul.locationId,
          name: location?.name || ul.locationId,
          type: location?.type || 'store',
        };
      });
    }

    // Fall back to legacy single location
    if (legacyLocationId && locations) {
      const location = legacyLocation || locations.find((l) => l.id === legacyLocationId);
      if (location) {
        return [{
          id: legacyLocationId,
          name: location.name || legacyLocationId,
          type: location.type || 'store',
        }];
      }
    }

    return [];
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (params: any) => `${params.row.firstName} ${params.row.lastName}`,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 220,
      renderCell: (params) => {
        const userRolesList = getUserRoles(params.row);
        if (!userRolesList.length) {
          return (
            <Typography variant="caption" color="text.secondary">
              No roles assigned
            </Typography>
          );
        }
        const maxDisplay = 2;
        const displayedRoles = userRolesList.slice(0, maxDisplay);
        const remainingCount = userRolesList.length - maxDisplay;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {displayedRoles.map((role) => (
              <Chip
                key={role.id}
                label={role.name}
                size="small"
                color={getRoleCategoryColor(role.category as RoleCategory)}
                variant={role.isSystemDefault ? 'filled' : 'outlined'}
              />
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={userRolesList
                  .slice(maxDisplay)
                  .map((r) => r.name)
                  .join(', ')}
              >
                <Chip label={`+${remainingCount}`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'userLocations',
      headerName: 'Locations',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const locationData = getLocationNames(
          params.row.userLocations,
          params.row.locationId,
          params.row.location
        );
        if (!locationData.length) {
          return (
            <Typography variant="caption" color="text.secondary">
              No location
            </Typography>
          );
        }
        const maxDisplay = 2;
        const displayedLocations = locationData.slice(0, maxDisplay);
        const remainingCount = locationData.length - maxDisplay;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {displayedLocations.map((loc) => (
              <Chip
                key={loc.id}
                label={loc.name}
                size="small"
                color={loc.type === 'store' ? 'primary' : 'secondary'}
                variant="outlined"
              />
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={locationData
                  .slice(maxDisplay)
                  .map((l) => l.name)
                  .join(', ')}
              >
                <Chip label={`+${remainingCount}`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/app/staff/edit/${params.row.id}`)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Staff</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/app/staff/create')}
        >
          Add Staff Member
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableToolbar
            title="Staff"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by name, email, phone..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns(roles || [], locations || [])}
            exportFileName="staff"
          />

          <DataGrid
            rows={filteredData}
            columns={columns}
            rowCount={filteredData.length}
            loading={isLoading}
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Staff Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this staff member? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
